/**
 * Presence heartbeat hook (#228 Phase 2). While the tab is open and visible it
 * pings /api/presence/ping every `intervalMs` (~PRESENCE_PING_MS). It pauses
 * when the tab is hidden (no point heartbeating a backgrounded tab) and resumes
 * on focus with an immediate ping so the user reads online again promptly.
 *
 * Best-effort and non-blocking: ping failures are swallowed in sendPresencePing.
 * Disabled when there is no logged-in user (nothing to attribute presence to).
 */
import { useEffect } from "react";
import { getToken } from "../lib/auth";
import { sendPresencePing } from "../lib/presence";

const DEFAULT_PING_MS = 30_000;

export function usePresence(intervalMs: number = DEFAULT_PING_MS): void {
  useEffect(() => {
    if (getToken() === null) return undefined;

    let timer: number | null = null;

    const stop = (): void => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const start = (): void => {
      if (timer !== null) return;
      void sendPresencePing();
      timer = window.setInterval(() => void sendPresencePing(), intervalMs);
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") stop();
      else start();
    };

    if (document.visibilityState !== "hidden") start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", start);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", start);
    };
  }, [intervalMs]);
}
